import React, { memo, useCallback } from 'react'
import { View, Text, Touchable, Avatar, Icon } from '@/components'
import { createStyles } from '@codeleap/styles'
import { textVariant } from '@/config'
import type { PollOption, PollVote } from 'stream-chat'

type PollOptionRowProps = {
  option: PollOption
  votes: number
  hasMyVote: boolean
  isWinner: boolean
  progress: number
  relevantVotes: PollVote[]
  canCastVote: boolean
  votingVisibility?: string
  onToggleVote: (optionId: string) => void
  isMyMessage: boolean
}

const PollOptionRowComponent = ({
  option,
  votes,
  hasMyVote,
  isWinner,
  progress,
  relevantVotes,
  canCastVote,
  votingVisibility,
  onToggleVote,
  isMyMessage,
}: PollOptionRowProps) => {
  const handleToggleVote = useCallback(() => {
    if (!canCastVote) return
    onToggleVote(option.id)
  }, [canCastVote, onToggleVote, option.id])

  return (
    <Touchable
      disabled={!canCastVote}
      onPress={handleToggleVote}
      propagate={false}
      style={[
        styles.optionRow,
        isMyMessage && styles.optionRowMine,
        !canCastVote && styles.optionRowDisabled,
      ]}
      debugName={`PollOption:${option.id}`}
    >
      <View style={styles.optionLeading}>
        <View
          style={[
            styles.checkbox,
            isMyMessage && styles.checkboxMine,
            hasMyVote && styles.checkboxChecked,
            hasMyVote && isMyMessage && styles.checkboxCheckedMine,
          ]}
        >
          {hasMyVote ? (
            <Icon
              name='check'
              style={[styles.checkboxIcon, isMyMessage && styles.checkboxIconMine]}
            />
          ) : null}
        </View>
      </View>
      <View style={styles.optionContent}>
        <View style={styles.optionHeader}>
          <Text
            text={option.text || t?.('Option') || 'Option'}
            style={[styles.optionText, isMyMessage && styles.optionTextMine]}
          />
          <View style={styles.voteInfo}>
            {votingVisibility === 'public' && relevantVotes.length > 0 ? (
              <View style={styles.avatars}>
                {relevantVotes.map((vote: PollVote, index: number) => {
                  const userImage = vote.user?.image
                  const imageUrl = typeof userImage === 'string' ? userImage : undefined

                  return (
                    <Avatar
                      key={`${option.id}-avatar-${vote.user?.id || `avatar-${index}`}`}
                      image={imageUrl}
                      name={vote.user?.name}
                      style={['small', styles.avatar]}
                      debugName='PollOption:Avatar'
                    />
                  )
                })}
              </View>
            ) : null}
            <Text
              text={String(votes)}
              style={[styles.voteCount, isMyMessage && styles.voteCountMine]}
            />
          </View>
        </View>

        <View style={[styles.progressBar, isMyMessage && styles.progressBarMine]}>
          <View
            style={[
              styles.progressFill,
              isMyMessage && styles.progressFillMine,
              { width: `${progress}%` },
            ]}
          />
        </View>
      </View>
    </Touchable>
  )
}

const areEqual = (prev: PollOptionRowProps, next: PollOptionRowProps) => {
  if (prev.option.id !== next.option.id) return false
  if (prev.option.text !== next.option.text) return false
  if (prev.votes !== next.votes) return false
  if (prev.hasMyVote !== next.hasMyVote) return false
  if (prev.isWinner !== next.isWinner) return false
  if (prev.progress !== next.progress) return false
  if (prev.canCastVote !== next.canCastVote) return false
  if (prev.isMyMessage !== next.isMyMessage) return false
  if (prev.votingVisibility !== next.votingVisibility) return false

  if (prev.relevantVotes.length !== next.relevantVotes.length) return false

  for (let i = 0; i < prev.relevantVotes.length; i++) {
    const prevVote = prev.relevantVotes[i]
    const nextVote = next.relevantVotes[i]
    if (prevVote?.id !== nextVote?.id || prevVote?.user?.id !== nextVote?.user?.id) {
      return false
    }
  }

  return true
}

export const PollOptionRow = memo(PollOptionRowComponent, areEqual)

const styles = createStyles(theme => ({
  container: {
    ...theme.presets.column,
    ...theme.spacing.gap(1.5),
    ...theme.spacing.padding(1.5),
    borderRadius: theme.radius.regular,
    backgroundColor: theme.colors.bgDefault,
  },
  containerMine: {
    backgroundColor: theme.baseColors.primarySolid500,
  },
  header: {
    ...theme.presets.column,
    ...theme.spacing.gap(0.25),
  },
  title: {
    ...textVariant('p3'),
    fontWeight: 600,
    color: theme.baseColors.onPrimarySolid500Heading,
  },
  titleMine: {
    color: theme.baseColors.onPrimarySolid500Heading,
  },
  subtitle: {
    ...textVariant('c3'),
    color: theme.baseColors.onPrimarySolid500Caption,
  },
  subtitleMine: {
    color: theme.baseColors.onPrimarySolid500Caption,
  },
  optionsWrapper: {
    ...theme.presets.column,
    ...theme.spacing.gap(1),
  },
  actions: {
    ...theme.presets.row,
    ...theme.spacing.gap(1),
  },
  actionButton: {
    touchableWrapper: {
      minWidth: theme.spacing.value(10),
    },
  },
  optionRow: {
    ...theme.presets.row,
    ...theme.presets.alignStart,
    ...theme.spacing.gap(1),
    ...theme.spacing.padding(1),
    borderRadius: theme.radius.regular,
    cursor: 'pointer',
    maxWidth: '100%',

    [theme.media.down('mobile')]: {
      ...theme.spacing.padding(1),
      ...theme.spacing.gap(0.75),
    },
  },
  optionRowMine: {
    backgroundColor: theme.baseColors.primaryTransparent100,
  },
  optionRowDisabled: {
    cursor: 'default',
    opacity: 0.6,
  },
  optionLeading: {
    ...theme.presets.center,
    height: theme.spacing.value(3),
  },
  checkbox: {
    ...theme.presets.center,
    ...theme.sized(2.5),
    borderRadius: 9999,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: theme.baseColors.primarySolid500,
    backgroundColor: 'transparent',
    flexShrink: 0,
  },
  checkboxMine: {
    borderColor: theme.baseColors.onPrimarySolid500Heading,
  },
  checkboxChecked: {
    backgroundColor: theme.baseColors.primarySolid500,
  },
  checkboxCheckedMine: {
    backgroundColor: theme.baseColors.onPrimarySolid500Heading,
  },
  checkboxIcon: {
    color: theme.baseColors.onPrimarySolid500Heading,
    ...theme.sized(1.5),
  },
  checkboxIconMine: {
    color: theme.baseColors.primarySolid500,
  },
  optionContent: {
    ...theme.presets.column,
    ...theme.spacing.gap(0.5),
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
  },
  optionHeader: {
    ...theme.presets.row,
    ...theme.presets.alignCenter,
    ...theme.presets.justifySpaceBetween,
    ...theme.spacing.gap(1),
    flexWrap: 'wrap',

    [theme.media.down('mobile')]: {
      ...theme.spacing.gap(0.5),
    },
  },
  optionText: {
    ...textVariant('p3'),
    color: theme.baseColors.onPrimarySolid50Heading,
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    flex: 1,
  },
  optionTextMine: {
    color: theme.baseColors.onPrimarySolid500Heading,
  },
  voteCount: {
    ...textVariant('c3'),
    color: theme.baseColors.onPrimarySolid50Caption,
  },
  voteCountMine: {
    color: theme.baseColors.onPrimarySolid500Caption,
  },
  progressBar: {
    position: 'relative',
    width: '100%',
    height: theme.spacing.value(0.75),
    borderRadius: theme.radius.small,
    overflow: 'hidden',
    backgroundColor: theme.baseColors.onPrimarySolid50CaptionSubtle,
  },
  progressBarMine: {
    backgroundColor: theme.baseColors.onPrimarySolid500CaptionSubtle,
  },
  progressFill: {
    position: 'absolute',
    top: 0,
    left: 0,
    height: '100%',
    backgroundColor: theme.baseColors.primarySolid500,
  },
  progressFillMine: {
    backgroundColor: theme.baseColors.onPrimarySolid500Heading,
  },
  voteInfo: {
    ...theme.presets.row,
    ...theme.presets.alignCenter,
    ...theme.spacing.gap(0.5),
    flexShrink: 0,

    [theme.media.down('mobile')]: {
      ...theme.spacing.gap(0.25),
    },
  },
  avatars: {
    ...theme.presets.row,
    ...theme.spacing.gap(0.5),
  },
  avatar: {
    wrapper: {
      width: 24,
      height: 24,
      minWidth: 24,
      minHeight: 24,
    },
  },
}))
